/**
 * Заставка перед входом: «Созвездие с перекличкой».
 *
 * Четыре действия за четыре секунды. Значки разделов слетаются со всех сторон
 * и связываются нитями в сеть; пятеро выходят вперёд и называют себя, пока
 * остальные отступают к краю; затем рой сходится в очертание формы входа, и
 * форма проявляется внутри собственного контура.
 *
 * Заставка ничего не задерживает. Форма всё это время в разметке и держит
 * фокус: первое же нажатие клавиши обрывает показ, и буква попадает в поле.
 * При системной настройке «меньше движения» заставки нет вовсе, а показывается
 * она один раз за сеанс — на пятидесятом входе красота становится помехой.
 *
 * Значки — контуры из lucide, того же набора, что и в интерфейсе: заставка и
 * приложение говорят одним языком форм. Рисуются на холсте как Path2D, поэтому
 * остаются вектором и не мылятся на плотных экранах.
 */

import * as React from "react";

/** Опорные точки расписания, мс. Собраны вместе: правка ритма — правка чисел. */
const T = {
  FLY: 1400, // слёт закончен, рой стоит облаком у середины
  CALL_OUT: 1180, // пятеро начинают выходить вперёд, рой расступается
  CALL_IN: 1560, // они на местах, начинается перекличка
  CALL_HOLD: 2520, // подписи стоят до этого мига
  CALL_BACK: 2900, // пятеро вернулись в рой
  SPREAD: 3400, // рой сложился в очертание формы — здесь появляется форма
  END: 4000,
} as const;

const COUNT = 46;
/** Дальше этого расстояния значки нитями не связываются. */
const LINK = 118;

const SEEN_KEY = "pt-intro-seen";

type Shape =
  | { kind: "path"; d: string }
  | { kind: "circle"; cx: number; cy: number; r: number }
  | { kind: "rect"; x: number; y: number; w: number; h: number; radius: number };

interface Glyph {
  label: string;
  shapes: Shape[];
  paths?: Path2D[];
}

const GLYPHS: Glyph[] = [
  {
    label: "Задачи",
    shapes: [
      { kind: "path", d: "m3 17 2 2 4-4" },
      { kind: "path", d: "m3 7 2 2 4-4" },
      { kind: "path", d: "M13 6h8" },
      { kind: "path", d: "M13 12h8" },
      { kind: "path", d: "M13 18h8" },
    ],
  },
  {
    label: "Проекты",
    shapes: [
      {
        kind: "path",
        d: "M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z",
      },
      { kind: "path", d: "M8 10v4" },
      { kind: "path", d: "M12 10v2" },
      { kind: "path", d: "M16 10v6" },
    ],
  },
  {
    label: "Отчёты",
    shapes: [
      { kind: "path", d: "M3 3v16a2 2 0 0 0 2 2h16" },
      { kind: "path", d: "M18 17V9" },
      { kind: "path", d: "M13 17V5" },
      { kind: "path", d: "M8 17v-3" },
    ],
  },
  {
    label: "Контакты",
    shapes: [
      { kind: "path", d: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" },
      { kind: "circle", cx: 9, cy: 7, r: 4 },
      { kind: "path", d: "M22 21v-2a4 4 0 0 0-3-3.87" },
      { kind: "path", d: "M16 3.13a4 4 0 0 1 0 7.75" },
    ],
  },
  {
    label: "Календарь",
    shapes: [
      { kind: "rect", x: 3, y: 4, w: 18, h: 18, radius: 2 },
      { kind: "path", d: "M16 2v4" },
      { kind: "path", d: "M3 10h18" },
      { kind: "path", d: "M8 2v4" },
      { kind: "path", d: "M17 14h-6" },
      { kind: "path", d: "M13 18H7" },
    ],
  },
  {
    label: "Учёт времени",
    shapes: [
      { kind: "circle", cx: 12, cy: 12, r: 10 },
      { kind: "path", d: "M12 6v6l4 2" },
    ],
  },
  {
    label: "Документы",
    shapes: [
      { kind: "path", d: "M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" },
      { kind: "path", d: "M14 2v4a2 2 0 0 0 2 2h4" },
      { kind: "path", d: "M10 9H8" },
      { kind: "path", d: "M16 13H8" },
      { kind: "path", d: "M16 17H8" },
    ],
  },
];

/**
 * Path2D строится один раз на значок.
 *
 * Пересборка на каждый кадр съедала бы весь бюджет кадра: полсотни значков по
 * пять контуров — это две с половиной сотни разборов строки шестьдесят раз
 * в секунду.
 */
function paths(glyph: Glyph): Path2D[] {
  if (glyph.paths === undefined) {
    glyph.paths = glyph.shapes.map((shape) => {
      if (shape.kind === "path") {
        return new Path2D(shape.d);
      }
      const path = new Path2D();
      if (shape.kind === "circle") {
        path.arc(shape.cx, shape.cy, shape.r, 0, Math.PI * 2);
      } else {
        path.roundRect(shape.x, shape.y, shape.w, shape.h, shape.radius);
      }
      return path;
    });
  }
  return glyph.paths;
}

interface Mote {
  glyph: Glyph;
  /** Названные: выходят вперёд и представляются. */
  hero: boolean;
  startX: number;
  startY: number;
  cloudX: number;
  cloudY: number;
  asideX: number;
  asideY: number;
  rimX: number;
  rimY: number;
  rowX: number;
  rowY: number;
  delay: number;
  backDelay: number;
  spin: number;
  x: number;
  y: number;
  arrived: number;
  called: number;
  tucked: number;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Резкое торможение у цели: летит быстро, встаёт мягко. */
function outCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

/** Доля отрезка времени, уже пройденная, со сглаживанием. */
function span(ms: number, from: number, to: number): number {
  return outCubic(clamp01((ms - from) / (to - from)));
}

function mix(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function token(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export interface LoginIntroProps {
  /** Очертание формы: рой сходится ровно в него, чтобы форма проявилась внутри. */
  target: React.RefObject<HTMLElement | null>;
  /** Форме пора появиться: зовётся один раз, в том числе при обрыве показа. */
  onReveal: () => void;
}

export function LoginIntro({ target, onReveal }: LoginIntroProps): React.ReactElement | null {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [gone, setGone] = React.useState(false);
  // Показ обрывается извне (щелчок, клавиша) и изнутри (время вышло) — ссылка
  // нужна, чтобы обработчики не пересоздавались и не перезапускали эффект
  const revealed = React.useRef(false);
  const onRevealRef = React.useRef(onReveal);
  onRevealRef.current = onReveal;

  React.useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d") ?? null;
    if (canvas === null || context === null) {
      onRevealRef.current();
      setGone(true);
      return;
    }

    let frame = 0;
    let closed = false;

    function reveal(): void {
      if (!revealed.current) {
        revealed.current = true;
        onRevealRef.current();
      }
    }

    /** Оборвать показ: человек пришёл работать, а не смотреть. */
    function skip(): void {
      if (closed) {
        return;
      }
      closed = true;
      cancelAnimationFrame(frame);
      reveal();
      setGone(true);
    }

    const ratio = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);

    // Холст лежит поверх всего экрана, поэтому его координаты совпадают
    // с координатами страницы, и очертание формы переносится как есть
    const box = target.current?.getBoundingClientRect();
    const cardW = box?.width ?? 360;
    const cardH = box?.height ?? 300;
    const cardX = box?.left ?? (width - cardW) / 2;
    const cardY = box?.top ?? (height - cardH) / 2;
    const centerX = cardX + cardW / 2;
    const centerY = cardY + cardH / 2;

    // Ряд переклички подстраивается под ширину: на узком экране пять подписей
    // налезают друг на друга, и перекличка превращается в кашу
    const heroCount = width < 620 ? 3 : 5;
    const gap = Math.min(124, width / (heroCount + 1));
    const fontSize = Math.max(10, Math.min(12.5, gap / 7.4));
    const perimeter = 2 * (cardW + cardH);

    const motes: Mote[] = [];
    for (let i = 0; i < COUNT; i += 1) {
      // Место в рамке — точка на периметре формы
      let along = (i / COUNT) * perimeter;
      let rimX: number;
      let rimY: number;
      if (along < cardW) {
        rimX = cardX + along;
        rimY = cardY;
      } else if ((along -= cardW) < cardH) {
        rimX = cardX + cardW;
        rimY = cardY + along;
      } else if ((along -= cardH) < cardW) {
        rimX = cardX + cardW - along;
        rimY = cardY + cardH;
      } else {
        rimX = cardX;
        rimY = cardY + cardH - (along - cardW);
      }

      // Облако у середины — не кольцо: разброс и по радиусу, и по углу
      const cloudAngle = Math.random() * Math.PI * 2;
      const cloudR = 46 + Math.random() * 128;
      // Кольцо по краю кадра, куда рой отходит на время переклички. Радиус
      // ограничен половиной экрана: без ограничения часть роя улетала за край
      // и в перекличке участвовала только та половина, что осталась видимой
      const asideR = 0.34 + Math.random() * 0.13;
      const asideRX = Math.min(width * asideR, width / 2 - 36);
      const asideRY = Math.min(height * (asideR + 0.06), height / 2 - 36);
      const from = (i / COUNT) * Math.PI * 2 + Math.random() * 0.9;
      const far = 320 + Math.random() * 360;
      const hero = i < heroCount;
      const glyph = GLYPHS[hero ? i : (i + heroCount) % GLYPHS.length];
      if (glyph === undefined) {
        continue;
      }

      motes.push({
        glyph,
        hero,
        startX: centerX + Math.cos(from) * far,
        startY: centerY + Math.sin(from) * far,
        // Названные ждут выхода ближе к середине: рывок из дальнего края роя
        // выглядел бы случайным
        cloudX: hero
          ? centerX + (i - (heroCount - 1) / 2) * gap * 0.55
          : centerX + Math.cos(cloudAngle) * cloudR,
        cloudY: hero ? centerY + 34 : centerY + Math.sin(cloudAngle) * cloudR * 0.78,
        asideX: centerX + Math.cos(cloudAngle) * asideRX,
        asideY: centerY + Math.sin(cloudAngle) * asideRY,
        rimX,
        rimY,
        rowX: centerX + (i - (heroCount - 1) / 2) * gap,
        rowY: centerY - 16,
        // Разный старт даёт рою неровность: одновременный прилёт выглядит
        // механически
        delay: Math.random() * 340,
        backDelay: Math.random() * 160,
        spin: (Math.random() - 0.5) * 1.2,
        x: 0,
        y: 0,
        arrived: 0,
        called: 0,
        tucked: 0,
      });
    }
    const heroes = motes.filter((mote) => mote.hero);

    const ink = token("--pt-text-primary");
    const thread = token("--pt-accent");
    const label = token("--pt-text-secondary");
    const paper = token("--pt-bg-canvas");
    const family = getComputedStyle(document.body).fontFamily;

    function place(ms: number): void {
      for (const mote of motes) {
        const arrived = span(ms - mote.delay, 0, T.FLY - 340);
        let x = mix(mote.startX, mote.cloudX, arrived);
        let y = mix(mote.startY, mote.cloudY, arrived);

        if (mote.hero) {
          // Выход вперёд и возвращение — по одной дуге, только вспять
          const out = span(ms, T.CALL_OUT, T.CALL_IN);
          const back = span(ms, T.CALL_HOLD, T.CALL_BACK);
          const at = out * (1 - back);
          x = mix(x, mote.rowX, at);
          y = mix(y, mote.rowY, at);
          mote.called = at;
        } else {
          // Рой расступается к краю и освобождает середину: иначе названные
          // значки оказываются поверх него, и сцена читается как две картинки,
          // наложенные друг на друга
          const aside = span(ms, T.CALL_OUT, T.CALL_IN + 140);
          x = mix(x, mote.asideX, aside);
          y = mix(y, mote.asideY, aside);
          mote.called = 0;
        }

        // Схождение продолжает движение с того места, где рой уже стоит
        mote.tucked = span(ms, T.CALL_BACK - 120 + mote.backDelay, T.SPREAD);
        mote.x = mix(x, mote.rimX, mote.tucked);
        mote.y = mix(y, mote.rimY, mote.tucked);
        mote.arrived = arrived;
      }
    }

    function paint(context2d: CanvasRenderingContext2D, mote: Mote, dim: number): void {
      // Значок мельчает, ложась в рамку: иначе очертание формы превратилось бы
      // в частокол и перебило бы саму форму
      const size = mix(24 + 12 * mote.called, 13, mote.tucked);
      const alpha = mote.arrived * dim * mix(1, 0.34, mote.tucked);
      if (alpha < 0.02) {
        return;
      }
      const scale = size / 24;
      context2d.save();
      context2d.globalAlpha = alpha;
      context2d.translate(mote.x, mote.y);
      context2d.rotate(mote.spin * (1 - mote.arrived) + mote.spin * 0.4 * mote.tucked);
      context2d.scale(scale, scale);
      context2d.translate(-12, -12);
      context2d.lineWidth = 2;
      context2d.lineCap = "round";
      context2d.lineJoin = "round";
      context2d.strokeStyle = ink;
      for (const path of paths(mote.glyph)) {
        context2d.stroke(path);
      }
      context2d.restore();
    }

    function draw(ms: number): void {
      const ctx = context as CanvasRenderingContext2D;
      ctx.clearRect(0, 0, width, height);
      place(ms);

      // Пока идёт перекличка, рой уходит на второй план: два равных по силе
      // события на экране спорят друг с другом, и не читается ни одно
      const calling = span(ms, T.CALL_OUT, T.CALL_IN) * (1 - span(ms, T.CALL_HOLD, T.CALL_BACK));
      const tail = 1 - clamp01((ms - T.SPREAD + 220) / 420);

      // Нити густые в слёте и редеющие к концу: сходящиеся значки попадают
      // в радиус связи все разом, и без этого сеть слипается в клубок
      const web = (1 - 0.55 * calling) * (1 - 0.8 * clamp01(ms / T.SPREAD)) * tail;
      if (web > 0.01) {
        ctx.lineWidth = 1;
        ctx.strokeStyle = thread;
        for (let i = 0; i < motes.length; i += 1) {
          for (let j = i + 1; j < motes.length; j += 1) {
            const first = motes[i];
            const second = motes[j];
            if (first === undefined || second === undefined) {
              continue;
            }
            const distance = Math.hypot(first.x - second.x, first.y - second.y);
            if (distance < LINK) {
              ctx.globalAlpha = (1 - distance / LINK) * 0.5 * web;
              ctx.beginPath();
              ctx.moveTo(first.x, first.y);
              ctx.lineTo(second.x, second.y);
              ctx.stroke();
            }
          }
        }
        ctx.globalAlpha = 1;
      }

      // Порядок важен: сначала рой, поверх — названные. Иначе рой ложится
      // на пятёрку и перекличка выглядит смазанной
      for (const mote of motes) {
        if (!mote.hero) {
          paint(ctx, mote, (1 - 0.74 * calling) * tail);
        }
      }
      for (const mote of heroes) {
        paint(ctx, mote, tail);
      }

      // Подпись отстаёт от значка: сначала предмет, потом имя
      const named =
        span(ms, T.CALL_IN - 60, T.CALL_IN + 260) *
        (1 - span(ms, T.CALL_HOLD - 120, T.CALL_BACK - 160));
      if (named > 0.02) {
        ctx.save();
        ctx.globalAlpha = named;
        ctx.textAlign = "center";
        ctx.font = `500 ${fontSize.toFixed(1)}px ${family}`;
        // Подложка цветом полотна: если позади всё же окажется значок, подпись
        // останется читаемой, а не сольётся с его штрихами
        ctx.lineWidth = 3.5;
        ctx.lineJoin = "round";
        ctx.strokeStyle = paper;
        ctx.fillStyle = label;
        for (const mote of heroes) {
          ctx.strokeText(mote.glyph.label, mote.x, mote.y + 34);
          ctx.fillText(mote.glyph.label, mote.x, mote.y + 34);
        }
        ctx.restore();
      }
    }

    let started = 0;
    function step(now: number): void {
      if (closed) {
        return;
      }
      if (started === 0) {
        started = now;
      }
      const ms = now - started;
      draw(ms);
      if (ms < T.END) {
        frame = requestAnimationFrame(step);
      }
    }
    frame = requestAnimationFrame(step);

    // Появление формы держится на таймерах, а не на кадрах анимации: во
    // вкладке, ушедшей в фон, браузер перестаёт выдавать кадры вовсе, и форма
    // не открылась бы никогда. Таймер в фоне тоже замедляется, но срабатывает
    const opening = window.setTimeout(reveal, T.SPREAD);
    const ending = window.setTimeout(() => {
      closed = true;
      cancelAnimationFrame(frame);
      setGone(true);
    }, T.END);

    // input — не то же, что keydown: вставка из буфера, автозаполнение
    // менеджером паролей и ввод через IME клавиш не порождают, а прятать
    // от них форму тем более незачем
    const interrupts = ["keydown", "pointerdown", "input"] as const;
    for (const name of interrupts) {
      window.addEventListener(name, skip);
    }
    return () => {
      closed = true;
      cancelAnimationFrame(frame);
      window.clearTimeout(opening);
      window.clearTimeout(ending);
      for (const name of interrupts) {
        window.removeEventListener(name, skip);
      }
    };
  }, [target]);

  if (gone) {
    return null;
  }
  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      // Размер задаётся и в CSS: у холста как у замещаемого элемента ширина
      // берётся из атрибута, а там пиксели устройства. При масштабе экрана,
      // отличном от 100 %, картинка растягивалась и уезжала от формы
      className="pointer-events-none fixed inset-0 h-full w-full z-[var(--pt-z-sticky)]"
    />
  );
}

/**
 * Показывать ли заставку.
 *
 * Один раз за сеанс и только тем, кто не просил систему двигаться поменьше:
 * на пятидесятом входе за день красота становится помехой.
 */
export function introWanted(): boolean {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return false;
  }
  try {
    return window.sessionStorage.getItem(SEEN_KEY) === null;
  } catch {
    // Хранилище закрыто настройками браузера — это не повод не показать вход
    return true;
  }
}

export function rememberIntroShown(): void {
  try {
    window.sessionStorage.setItem(SEEN_KEY, "1");
  } catch {
    // Не запомнили — заставка повторится, но вход от этого не сломается
  }
}
